import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobal } from 'hooks/useGlobal';

/**
 * How many `WorkspaceSidePanel`s are mounted right now, as a `useGlobal` value. The layout reads
 * it to fold the navigation to its rail while an AI chat is docked beside the workspace.
 *
 * A count, not a boolean: two panels can overlap for a render (one route's chat unmounting as the
 * next one's mounts), and a flag set by one and cleared by the other would be left wrong.
 */
export const SIDE_PANEL_OPEN = 'SIDE_PANEL_OPEN';

/** The cache key `useGlobal` stores the count under. It namespaces every key with 'USE_GLOBAL'. */
const SIDE_PANEL_OPEN_KEY = ['USE_GLOBAL', SIDE_PANEL_OPEN];

export const useIsSidePanelOpen = () => {
  const [count] = useGlobal<number>(SIDE_PANEL_OPEN, 0);
  return count > 0;
};

/**
 * Counts the calling component in for as long as it is mounted.
 *
 * Written through the query client rather than `useGlobal`'s setter: that setter applies a
 * functional update to the value its own hook last rendered, so two panels mounting in one commit
 * would both read 0 and both write 1. `setQueryData` updates from what is in the cache.
 */
export const useRegisterSidePanel = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.setQueryData<number>(SIDE_PANEL_OPEN_KEY, (count = 0) => count + 1);
    return () => {
      queryClient.setQueryData<number>(SIDE_PANEL_OPEN_KEY, (count = 0) => Math.max(0, count - 1));
    };
  }, [queryClient]);
};
